import React, { useEffect, useState, useCallback, useRef, memo } from "react";
import { useUser } from "../contexts/UserContext";
import { User, USER_TYPES, UserTypeValue } from "../types/userType";
import styles from "./UserList.module.css";
import config from "../config";
import Button from "../components/UI/Button";
import Input from "../components/UiElements/Input";
import DSButton from "../components/UiElements/Button";
import useIsSuperAdmin from "../hooks/useIsSuperAdmin";
import useIsAdmin from "../hooks/useIsAdmin";
import useIsRental from "../hooks/useIsRental";
import XCloseIcon from "../components/icons/XCloseIcon";
import AddIcon from "../components/icons/AddIcon";
import DropdownSelect from "../components/DropdownSelect/DropdownSelect";

type RoleFilterOption = { label: string; value: string };

const ROLE_FILTER_OPTIONS: RoleFilterOption[] = [
  { label: "כל ההרשאות", value: "" },
  { label: "Super Admin", value: USER_TYPES.SUPER_ADMIN },
  { label: "Admin", value: USER_TYPES.ADMIN },
  { label: "Inspection", value: USER_TYPES.INSPECTION },
  { label: "Rental", value: USER_TYPES.RENTAL },
  { label: "Operate", value: USER_TYPES.OPERATE },
  { label: "Freesbe", value: USER_TYPES.FREESBE },
  { label: "Chatbot", value: USER_TYPES.CHATBOT },
  { label: "Regular", value: USER_TYPES.REGULAR },
];

// Memoized phone search input component
const PhoneSearchInput = memo(
  React.forwardRef<
    HTMLInputElement,
    {
      phoneSearch: string;
      setPhoneSearch: (v: string) => void;
      searchActive: boolean;
      handleClearSearch: () => void;
      isSuperAdmin: boolean;
      onAddUser: () => void;
      roleFilter: string;
      onRoleFilterChange: (value: string) => void;
    }
  >(
    (
      {
        phoneSearch,
        setPhoneSearch,
        searchActive,
        handleClearSearch,
        isSuperAdmin,
        onAddUser,
        roleFilter,
        onRoleFilterChange,
      },
      ref
    ) => {
      const selectedRole =
        ROLE_FILTER_OPTIONS.find((o) => o.value === roleFilter) ||
        ROLE_FILTER_OPTIONS[0];

      return (
        <form
          className={styles.searchForm}
          style={{ marginBottom: 16 }}
          onSubmit={(e) => e.preventDefault()}
        >
          <Input
            ref={ref}
            type="text"
            placeholder="חפש לפי שם, אימייל או טלפון"
            value={phoneSearch}
            onChange={(e) => setPhoneSearch(e.target.value)}
            className={styles.searchInput}
            rtl
            size="medium"
            color="outline"
          />
          <span style={{ display: searchActive ? undefined : "none" }}>
            <DSButton
              buttonType="button"
              label="נקה"
              size="medium"
              color="outline"
              onClick={handleClearSearch}
              width={"100px"}
            />
          </span>
          <DropdownSelect
            name="user-role-filter"
            className={styles.roleFilter}
            placeholder="הרשאות"
            values={[selectedRole]}
            options={[...ROLE_FILTER_OPTIONS]}
            onChange={(selected: RoleFilterOption[]) => {
              const next = selected?.[0]?.value ?? "";
              onRoleFilterChange(next);
            }}
            color="outline"
            size="medium"
            width={160}
            dropdownMaxHeight={220}
          />
          {isSuperAdmin && (
            <DSButton
              buttonType="button"
              label="הוסף משתמש"
              size="medium"
              icon={<AddIcon width={18} height={18} />}
              onClick={onAddUser}
              width={"160px"}
            />
          )}
        </form>
      );
    }
  )
);

const UserList: React.FC = () => {
  const { userType, users, getUsers, loading, loadingMore, hasMore } =
    useUser();
  const [reloadUsers, setReloadUsers] = useState(0);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<UserTypeValue[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [phoneSearch, setPhoneSearch] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  /** True after user has typed a phone query; used to refetch unfiltered list when search is cleared. */
  const hadPhoneFilterRef = useRef(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUsername, setEditUsername] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editSendNewChatEmail, setEditSendNewChatEmail] = useState(false);
  const [editSendFuelCodeStatusEmail, setEditSendFuelCodeStatusEmail] =
    useState(false);
  const [editError, setEditError] = useState("");
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newRoles, setNewRoles] = useState<UserTypeValue[]>([]);
  const [newUserError, setNewUserError] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showPrivilegeHelpModal, setShowPrivilegeHelpModal] = useState(false);
  const [privilegeDocs, setPrivilegeDocs] = useState<
    {
      roleKey: string;
      label: string;
      description: string;
      sortOrder: number;
    }[]
  >([]);
  const [privilegeDocsLoading, setPrivilegeDocsLoading] = useState(false);
  const [privilegeDocsError, setPrivilegeDocsError] = useState("");
  const [privilegeDocsSaving, setPrivilegeDocsSaving] = useState(false);

  const isSuperAdmin = useIsSuperAdmin();
  const isAdmin = useIsAdmin();
  const isRental = useIsRental() && !isSuperAdmin && !isAdmin;

  const roleFilterRef = useRef(roleFilter);
  roleFilterRef.current = roleFilter;
  const phoneSearchRef = useRef(phoneSearch);
  phoneSearchRef.current = phoneSearch;

  const fetchUsersPage = useCallback(
    (page: number, reset: boolean) => {
      const phone = phoneSearchRef.current.trim();
      const role = roleFilterRef.current;
      getUsers(page, reset, phone || undefined, role || undefined);
    },
    [getUsers]
  );

  // Initial load
  useEffect(() => {
    fetchUsersPage(1, true);
  }, []);

  // Handle reloads when reloadUsers changes (but not on initial mount)
  useEffect(() => {
    if (reloadUsers > 0) {
      // Only reload if it's been triggered (counter > 0)
      // Clear search when reloading
      setPhoneSearch("");
      setSearchActive(false);
      hadPhoneFilterRef.current = false;
      fetchUsersPage(1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadUsers]);

  useEffect(() => {
    // Empty query: reset filter UI; refetch full list only if we had applied a phone filter before
    if (phoneSearch === "") {
      setSearchActive(false);
      if (hadPhoneFilterRef.current) {
        hadPhoneFilterRef.current = false;
        fetchUsersPage(1, true);
        return;
      }
      return;
    }

    hadPhoneFilterRef.current = true;

    // Debounced live search
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      fetchUsersPage(1, true);
      setSearchActive(true);
    }, 400);
    // Cleanup on unmount
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneSearch]);

  const handleClearSearch = () => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = null;
    }
    setPhoneSearch("");
    setSearchActive(false);
  };

  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value);
  };

  const skipNextRoleFetchRef = useRef(true);

  useEffect(() => {
    if (skipNextRoleFetchRef.current) {
      skipNextRoleFetchRef.current = false;
      return;
    }
    fetchUsersPage(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchUsersPage(nextPage, false);
    }
  }, [currentPage, loadingMore, hasMore, fetchUsersPage]);

  const handleScroll = useCallback(() => {
    if (tableContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        tableContainerRef.current;
      // Load more when user has scrolled to within 100px of the bottom
      if (scrollHeight - scrollTop <= clientHeight + 100) {
        loadMore();
      }
    }
  }, [loadMore]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const handleChangeRole = (user: User) => {
    setSelectedUser(user);
    setSelectedRoles([...user.userType]); // Initialize with current user roles
    setShowRoleModal(true);
  };

  const handleRoleToggle = (role: UserTypeValue) => {
    setSelectedRoles((prevRoles) => {
      if (prevRoles.includes(role)) {
        // Remove role if already selected
        return prevRoles.filter((r) => r !== role);
      } else {
        // Add role if not selected
        return [...prevRoles, role];
      }
    });
  };

  const handleRoleUpdate = () => {
    if (selectedUser) {
      console.log(selectedUser._id);
      fetch(`${config.backendUrl}/users/update-user`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({
          userType: selectedRoles,
          userId: selectedUser._id,
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          setReloadUsers((r) => r + 1);
          setShowRoleModal(false);
          setSelectedUser(null);
          setSelectedRoles([]);
        });
    }
  };

  const handleModalCancel = () => {
    setShowRoleModal(false);
    setSelectedUser(null);
    setSelectedRoles([]);
  };

  const openPrivilegeHelpModal = async () => {
    setShowPrivilegeHelpModal(true);
    setPrivilegeDocsError("");
    setPrivilegeDocsLoading(true);
    try {
      const res = await fetch(
        `${config.backendUrl}/users/role-privilege-docs`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          (data as { message?: string }).message || "שגיאה בטעינת הסבר ההרשאות"
        );
      }
      setPrivilegeDocs(Array.isArray(data.docs) ? data.docs : []);
    } catch (err) {
      setPrivilegeDocs([]);
      setPrivilegeDocsError(
        err instanceof Error ? err.message : "שגיאה בטעינת הסבר ההרשאות"
      );
    } finally {
      setPrivilegeDocsLoading(false);
    }
  };

  const closePrivilegeHelpModal = () => {
    setShowPrivilegeHelpModal(false);
    setPrivilegeDocsError("");
  };

  const updatePrivilegeDocField = (
    roleKey: string,
    field: "label" | "description",
    value: string
  ) => {
    setPrivilegeDocs((prev) =>
      prev.map((doc) =>
        doc.roleKey === roleKey ? { ...doc, [field]: value } : doc
      )
    );
  };

  const savePrivilegeDocs = async () => {
    setPrivilegeDocsSaving(true);
    setPrivilegeDocsError("");
    try {
      const res = await fetch(
        `${config.backendUrl}/users/role-privilege-docs`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({
            docs: privilegeDocs.map((doc) => ({
              roleKey: doc.roleKey,
              label: doc.label,
              description: doc.description,
              sortOrder: doc.sortOrder,
            })),
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          (data as { message?: string }).message || "שגיאה בשמירת הסבר ההרשאות"
        );
      }
      setPrivilegeDocs(Array.isArray(data.docs) ? data.docs : privilegeDocs);
    } catch (err) {
      setPrivilegeDocsError(
        err instanceof Error ? err.message : "שגיאה בשמירת הסבר ההרשאות"
      );
    } finally {
      setPrivilegeDocsSaving(false);
    }
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (selectedUser) {
      fetch(`${config.backendUrl}/users/delete-user`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ userId: selectedUser._id }),
      })
        .then((res) => res.json())
        .then((data) => {
          setReloadUsers((r) => r + 1);
          setShowDeleteModal(false);
          setSelectedUser(null);
        });
    }
  };

  const handleDeleteModalCancel = () => {
    setShowDeleteModal(false);
    setSelectedUser(null);
  };

  const formatUserTypes = (userTypes: UserTypeValue[]) => {
    return userTypes
      .map((type) => {
        switch (type) {
          case USER_TYPES.SUPER_ADMIN:
            return "Super Admin";
          case USER_TYPES.ADMIN:
            return "Admin";
          case USER_TYPES.INSPECTION:
            return "Inspection";
          case USER_TYPES.REGULAR:
            return "Regular";
          case USER_TYPES.CHATBOT:
            return "Chatbot";
          case USER_TYPES.RENTAL:
            return "Rental";
          case USER_TYPES.OPERATE:
            return "Operate";
          case USER_TYPES.FREESBE:
            return "Freesbe";
          default:
            return type;
        }
      })
      .join(", ");
  };

  const validateEmail = (email: string) => {
    if (!email || email.trim() === "") return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getDisplayEmail = (email: string) => {
    if (!email || !validateEmail(email)) {
      return "-";
    }
    return email;
  };

  // Focus the input after users update if search is active
  useEffect(() => {
    if (searchActive && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [users, searchActive]);

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditUsername(user.username || "");
    setEditEmail(user.email || "");
    setEditPassword("");
    setEditSendNewChatEmail(user.sendNewChatEmail || false);
    setEditSendFuelCodeStatusEmail(user.sendFuelCodeStatusEmail || false);
    setEditError("");
    setShowEditModal(true);
  };

  const handleEditModalCancel = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    setEditUsername("");
    setEditEmail("");
    setEditPassword("");
    setEditSendNewChatEmail(false);
    setEditSendFuelCodeStatusEmail(false);
    setEditError("");
  };

  const handleEditUpdate = () => {
    if (selectedUser) {
      setEditError("");

      // Validate email if provided
      if (editEmail && !validateEmail(editEmail)) {
        setEditError("כתובת אימייל לא תקינה");
        return;
      }

      const updateData = {
        userId: selectedUser._id,
        username: editUsername,
        email: editEmail || null,
        password: editPassword || undefined,
        sendNewChatEmail: editSendNewChatEmail,
        sendFuelCodeStatusEmail: editSendFuelCodeStatusEmail,
      };

      fetch(`${config.backendUrl}/users/update-user`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify(updateData),
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("data", data);
          if (data.error && data.error.toLowerCase().includes("username")) {
            setEditError("שם משתמש כבר קיים. בחר שם אחר.");
            return;
          }
          if (data.error && data.error.toLowerCase().includes("email")) {
            setEditError("כתובת אימייל כבר קיימת. בחר כתובת אחרת.");
            return;
          }
          setReloadUsers((r) => r + 1);
          setShowEditModal(false);
          setSelectedUser(null);
          setEditUsername("");
          setEditEmail("");
          setEditPassword("");
          setEditSendNewChatEmail(false);
          setEditSendFuelCodeStatusEmail(false);
          setEditError("");
        })
        .catch(() => {
          console.log("error");
          setEditError("שגיאה בעדכון המשתמש. נסה שוב.");
        });
    }
  };

  const resetAddUserForm = () => {
    setNewUsername("");
    setNewEmail("");
    setNewPassword("");
    setNewPhone("");
    setNewRoles([]);
    setNewUserError("");
  };

  const handleOpenAddUser = () => {
    resetAddUserForm();
    setShowAddUserModal(true);
  };

  const handleAddUserModalCancel = () => {
    setShowAddUserModal(false);
    resetAddUserForm();
  };

  const handleNewRoleToggle = (role: UserTypeValue) => {
    setNewRoles((prevRoles) => {
      if (prevRoles.includes(role)) {
        return prevRoles.filter((r) => r !== role);
      }
      return [...prevRoles, role];
    });
  };

  const handleAddUser = () => {
    setNewUserError("");

    if (
      !newUsername.trim() ||
      !newPassword.trim() ||
      newRoles.length === 0
    ) {
      setNewUserError("יש למלא את כל השדות הנדרשים ולבחור לפחות הרשאה אחת");
      return;
    }

    if (newEmail && !validateEmail(newEmail)) {
      setNewUserError("כתובת אימייל לא תקינה");
      return;
    }

    const phone = newPhone.trim();
    const username = newUsername.trim();
    const body: Record<string, unknown> = {
      username,
      name: phone || username,
      password: newPassword,
      userType: newRoles,
    };

    if (phone) {
      body.phone = phone;
    }

    if (newEmail.trim()) {
      body.email = newEmail.trim();
    }

    fetch(`${config.backendUrl}/users/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
      body: JSON.stringify(body),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const message =
            typeof data.message === "string" ? data.message.toLowerCase() : "";
          const errorRaw =
            typeof data.error === "string"
              ? data.error
              : data.error && typeof data.error === "object"
                ? `${JSON.stringify(data.error)} ${Object.keys(data.error.keyPattern || {}).join(" ")}`
                : "";
          const error = errorRaw.toLowerCase();
          const combined = `${message} ${error}`;
          if (combined.includes("phone")) {
            setNewUserError("מספר טלפון כבר קיים. בחר מספר אחר.");
            return;
          }
          if (combined.includes("username")) {
            setNewUserError("שם משתמש כבר קיים. בחר שם אחר.");
            return;
          }
          if (combined.includes("email")) {
            setNewUserError("כתובת אימייל כבר קיימת. בחר כתובת אחרת.");
            return;
          }
          setNewUserError("שגיאה בהוספת המשתמש. נסה שוב.");
          return;
        }
        setShowAddUserModal(false);
        resetAddUserForm();
        setReloadUsers((r) => r + 1);
      })
      .catch((err) => {
        setNewUserError("שגיאה בהוספת המשתמש. נסה שוב.");
      });
  };

  // if (loading) {
  //   return (
  //     <div className={styles.loadingContainer}>
  //       <p>Loading users...</p>
  //     </div>
  //   );
  // }

  return (
    <div className={styles.container}>
      {/* Phone Search Input */}
      <PhoneSearchInput
        ref={searchInputRef}
        phoneSearch={phoneSearch}
        setPhoneSearch={setPhoneSearch}
        searchActive={searchActive}
        handleClearSearch={handleClearSearch}
        isSuperAdmin={isSuperAdmin}
        onAddUser={handleOpenAddUser}
        roleFilter={roleFilter}
        onRoleFilterChange={handleRoleFilterChange}
      />
      {!Array.isArray(users) || users.length === 0 ? (
        <p className={styles.noUsersMessage}>
          {!loading && "לא נמצאו משתמשים"}
        </p>
      ) : (
        <div ref={tableContainerRef} className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.tableHeader}>
                <th className={styles.tableHeaderCell}>שם משתמש</th>
                <th className={styles.tableHeaderCell}>אימייל</th>
                <th className={styles.tableHeaderCell}>טלפון</th>
                <th
                  className={
                    styles.tableHeaderCell + " " + styles.roleHeaderCell
                  }
                >
                  <span className={styles.roleHeaderLabel}>
                    הרשאות
                    <button
                      type="button"
                      className={styles.roleHelpButton}
                      aria-label="הסבר על הרשאות"
                      title="הסבר על הרשאות"
                      onClick={(e) => {
                        e.stopPropagation();
                        openPrivilegeHelpModal();
                      }}
                    >
                      ?
                    </button>
                  </span>
                </th>
                <th
                  className={
                    styles.tableHeaderCell + " " + styles.actionsHeaderCell
                  }
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {(users || []).map((user, index) => (
                <tr key={user._id || index} className={styles.tableRow}>
                  <td
                    className={styles.tableCell}
                    style={{ filter: isRental ? "blur(3px)" : "inherit" }}
                  >
                    {isRental ? "username" : user.username}
                  </td>
                  <td
                    className={styles.tableCell}
                    style={{ filter: isRental ? "blur(3px)" : "inherit" }}
                  >
                    {isRental
                      ? "email@example.com"
                      : getDisplayEmail(user.email)}
                  </td>
                  <td
                    className={styles.phoneCell}
                    style={{ filter: isRental ? "blur(3px)" : "inherit" }}
                  >
                    {isRental ? "0525544454" : user.phone || "-"}
                  </td>
                  <td
                    onClick={() => isSuperAdmin && handleChangeRole(user)}
                    className={styles.tableCell}
                    style={{ cursor: isSuperAdmin ? "pointer" : "default" }}
                  >
                    <span className={styles.roleBadge}>
                      {formatUserTypes(user.userType || [])}
                    </span>
                  </td>
                  <td className={styles.actionCell}>
                    <Button
                      color="blue"
                      size="small"
                      onClick={() => isSuperAdmin && handleEditUser(user)}
                      className={
                        styles.editButton +
                        " " +
                        (isSuperAdmin ? "" : styles.disabled)
                      }
                    >
                      ערוך
                    </Button>
                    <Button
                      color="red"
                      size="small"
                      onClick={() => isSuperAdmin && handleDeleteUser(user)}
                      className={
                        styles.deleteButton +
                        " " +
                        (isSuperAdmin ? "" : styles.disabled)
                      }
                    >
                      מחק
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Loading more indicator */}
          {loadingMore && (
            <div className={styles.loadingMoreIndicator}>
              <p className={styles.loadingMoreText}>טוען משתמשים נוספים...</p>
            </div>
          )}

          {/* End of data indicator */}
          {!hasMore && users.length > 0 && (
            <div className={styles.endOfDataIndicator}>
              אין עוד משתמשים לטעינה
            </div>
          )}
        </div>
      )}

      {/* Role Privilege Help Modal */}
      {showPrivilegeHelpModal && (
        <div className={styles.modalOverlay} onClick={closePrivilegeHelpModal}>
          <div
            className={`${styles.modalContent} ${styles.privilegeHelpModal}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={styles.modalClose}
              onClick={closePrivilegeHelpModal}
              aria-label="סגור"
            >
              <XCloseIcon width={18} height={18} />
            </button>
            <h3 className={styles.modalTitle}>הסבר הרשאות</h3>
            {privilegeDocsLoading ? (
              <p className={styles.privilegeHelpLoading}>טוען...</p>
            ) : privilegeDocsError && privilegeDocs.length === 0 ? (
              <p className={styles.editError}>{privilegeDocsError}</p>
            ) : (
              <div className={styles.privilegeHelpList}>
                {privilegeDocs.map((doc) => (
                  <div key={doc.roleKey} className={styles.privilegeHelpItem}>
                    {isSuperAdmin ? (
                      <>
                        <p className={styles.privilegeHelpRoleKey}>
                          {doc.roleKey}
                        </p>
                        <label className={styles.editLabel} htmlFor={`priv-label-${doc.roleKey}`}>
                          שם התפקיד
                        </label>
                        <Input
                          id={`priv-label-${doc.roleKey}`}
                          type="text"
                          value={doc.label}
                          onChange={(e) =>
                            updatePrivilegeDocField(
                              doc.roleKey,
                              "label",
                              e.target.value
                            )
                          }
                          className={styles.editInput}
                          rtl
                          size="medium"
                          color="outline"
                        />
                        <label
                          className={styles.editLabel}
                          htmlFor={`priv-desc-${doc.roleKey}`}
                        >
                          תיאור ההרשאות
                        </label>
                        <textarea
                          id={`priv-desc-${doc.roleKey}`}
                          className={styles.privilegeHelpTextarea}
                          value={doc.description}
                          rows={4}
                          onChange={(e) =>
                            updatePrivilegeDocField(
                              doc.roleKey,
                              "description",
                              e.target.value
                            )
                          }
                        />
                      </>
                    ) : (
                      <>
                        <h4 className={styles.privilegeHelpRoleLabel}>
                          {doc.label}
                        </h4>
                        <p className={styles.privilegeHelpDescription}>
                          {doc.description}
                        </p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            {privilegeDocsError && privilegeDocs.length > 0 && (
              <p className={styles.editError}>{privilegeDocsError}</p>
            )}
            <div className={styles.modalActions}>
              <Button
                color="gray"
                size="medium"
                onClick={closePrivilegeHelpModal}
              >
                סגור
              </Button>
              {isSuperAdmin && privilegeDocs.length > 0 && (
                <Button
                  color="green"
                  size="medium"
                  onClick={savePrivilegeDocs}
                  className={styles.updateButton}
                  disabled={privilegeDocsSaving || privilegeDocsLoading}
                >
                  {privilegeDocsSaving ? "שומר..." : "שמור"}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Role Change Modal */}
      {showRoleModal && selectedUser && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>
              הרשאות ניהול משתמש {selectedUser.username}
            </h3>

            <div className={styles.roleSelectionContainer}>
              {Object.entries(USER_TYPES)
                .filter(([key, value]) => value !== USER_TYPES.REGULAR)
                .map(([key, value]) => {
                  const isSelected = selectedRoles.includes(value);
                  return (
                    <Button
                      key={value}
                      color={isSelected ? "blue" : "gray"}
                      size="medium"
                      onClick={() => handleRoleToggle(value)}
                      className={`${styles.roleButton} ${isSelected ? styles.selected : ""
                        }`}
                    >
                      {key
                        .replace("_", " ")
                        .toLowerCase()
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Button>
                  );
                })}
            </div>

            <div className={styles.modalActions}>
              <Button color="gray" size="medium" onClick={handleModalCancel}>
                ביטול
              </Button>
              <Button
                color="green"
                size="medium"
                onClick={handleRoleUpdate}
                className={styles.updateButton}
                disabled={selectedRoles.length === 0}
              >
                עדכון
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>
              האם אתה בטוח שברצונך למחוק את המשתמש {selectedUser.username}?
            </h3>
            <div className={styles.modalActions}>
              <Button color="gray" size="medium" onClick={handleDeleteModalCancel}>
                ביטול
              </Button>
              <Button color="red" size="medium" onClick={handleConfirmDelete}>
                מחק
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>
              עריכת שם משתמש {selectedUser.username}
            </h3>
            {editError && <div className={styles.editError}>{editError}</div>}
            <div className={styles.editFormGroup}>
              <label className={styles.editLabel}>שם משתמש:</label>
              <input
                className={styles.editInput}
                type="text"
                value={editUsername}
                onChange={(e) => setEditUsername(e.target.value)}
              />
            </div>
            <div className={styles.editFormGroup}>
              <label className={styles.editLabel}>אימייל:</label>
              <input
                style={{ direction: "ltr" }}
                className={styles.editInput}
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                placeholder="example@domain.com"
              />
            </div>
            <div className={styles.editFormGroup}>
              <label className={styles.editLabel}>
                <input
                  type="checkbox"
                  checked={editSendNewChatEmail}
                  onChange={(e) => setEditSendNewChatEmail(e.target.checked)}
                  className={styles.editCheckbox}
                />
                שלח אימייל התראה על צ'אט חדש
              </label>
              <label className={styles.editLabel}>
                <input
                  type="checkbox"
                  checked={editSendFuelCodeStatusEmail}
                  onChange={(e) =>
                    setEditSendFuelCodeStatusEmail(e.target.checked)
                  }
                  className={styles.editCheckbox}
                />
                שלח אימייל התראה על כמות מספרי תדלוק שנותרו
              </label>
            </div>



            <div className={styles.editFormGroup}>
              <label className={styles.editLabel}>סיסמה חדשה:</label>
              <input
                className={styles.editInput}
                type="password"
                value={editPassword}
                onChange={(e) => setEditPassword(e.target.value)}
                placeholder="(לא חובה)"
              />
            </div>

            <div className={styles.modalActions}>
              <Button color="gray" size="medium" onClick={handleEditModalCancel}>
                ביטול
              </Button>
              <Button
                color="green"
                size="medium"
                onClick={handleEditUpdate}
                className={styles.updateButton}
                disabled={!editUsername.trim()}
              >
                עדכן
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <button
              type="button"
              className={`ui-elements-button ${styles.modalClose}`}
              aria-label="סגור"
              onClick={handleAddUserModalCancel}
            >
              <XCloseIcon width={20} height={20} />
            </button>
            <h3 className={styles.modalTitle}>הוספת משתמש</h3>

            <div className={styles.editFormGroup}>
              <label className={styles.editLabel}>שם משתמש:</label>
              <input
                className={styles.editInput}
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
              />
            </div>
            <div className={styles.editFormGroup}>
              <label className={styles.editLabel}>אימייל:</label>
              <input
                style={{ direction: "ltr" }}
                className={styles.editInput}
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="example@domain.com"
              />
            </div>
            <div className={styles.editFormGroup}>
              <label className={styles.editLabel}>סיסמה:</label>
              <input
                className={styles.editInput}
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className={styles.editFormGroup}>
              <label className={styles.editLabel}>טלפון:</label>
              <input
                style={{ direction: "ltr" }}
                className={styles.editInput}
                type="text"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
              />
            </div>
            <div className={styles.editFormGroup}>
              <label className={styles.editLabel}>הרשאות ניהול:</label>
              <div className={styles.roleSelectionContainer}>
                {Object.entries(USER_TYPES)
                  .filter(([, value]) => value !== USER_TYPES.REGULAR)
                  .map(([key, value]) => {
                    const isSelected = newRoles.includes(value);
                    return (
                      <Button
                        key={value}
                        color={isSelected ? "blue" : "gray"}
                        size="medium"
                        onClick={() => handleNewRoleToggle(value)}
                        className={`${styles.roleButton} ${
                          isSelected ? styles.selected : ""
                        }`}
                      >
                        {key
                          .replace("_", " ")
                          .toLowerCase()
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </Button>
                    );
                  })}
              </div>
            </div>
            {newUserError && (
              <div className={styles.editError}>{newUserError}</div>
            )}
            <div className={styles.modalActions}>
              <Button
                color="gray"
                size="medium"
                onClick={handleAddUserModalCancel}
              >
                ביטול
              </Button>
              <Button
                color="green"
                size="medium"
                onClick={handleAddUser}
                className={styles.updateButton}
                disabled={
                  !newUsername.trim() ||
                  !newPassword.trim() ||
                  newRoles.length === 0
                }
              >
                הוסף
              </Button>
            </div>
         
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
